import { WarningRounded } from "@mui/icons-material";
import { useMediaQuery, Typography, Container } from "@mui/material";
import {
  mobileWidth,
  formatUtcDate,
  BulkBoxIcon,
  ScanningIndicator,
  useScanIndicator,
  useToast,
  ErrorPanel,
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowSkeleton,
  ASTableV2RowEmpty
} from "@qubit/autoparts";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";

import { searchProduct, getBarcodeValue } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGetPutawayTasksWithPagingQuery } from "~/redux/warehouse/putAwayTasks.hooks";
import { PutAwayTaskSummaryDto } from "~/types/api";

import { PutawaySearch } from "./PutawaySearch";
import { PutawayToolbar } from "./PutawayToolbar";
import {
  clearSelectedPutawayTasks,
  selectPutawayTasks,
  setPage,
  setSearch
} from "./putaway.slice";

export type PutawayProps = { viewTitle?: ViewNameTranslation };

export const Putaway = ({ viewTitle }: PutawayProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { errorToast } = useToast();

  const navigate = useFcNavigate();

  const { setMenuItems, setToolbar } = useNavbar({
    centerComponent: useMemo(() => <PutawaySearch />, []),
    viewTitle
  });

  const page = useAppSelector((state) => state.putaway.page);
  const searchText = useAppSelector((state) => state.putaway.search);
  const selectedPutawayTasks = useAppSelector(
    (state) => state.putaway.selectedPutawayTasks
  );

  const isMobile = useMediaQuery(mobileWidth);
  const trackedPageName = useMixpanelPageName();

  const limit = isMobile ? 6 : 11;

  const { data, isFetching, error, refetch } =
    useGetPutawayTasksWithPagingQuery(
      {
        status: ["scheduled"],
        limit,
        offset: (page - 1) * limit,
        ...(searchText && { search: searchText }),
        binType: [],
        temperatureZone: []
      },
      { refetchOnMountOrArgChange: true }
    );

  const putawayInventory = data?.putAwayTasks;
  const putawayInventoryCount = data?.putAwayTasksCount;

  const totalPageCount = putawayInventoryCount
    ? Math.ceil(putawayInventoryCount / limit)
    : 0;

  const [scanState, setScanState] = useScanIndicator();
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);

  useKeyDownHandler();
  useBarcodeScanner({
    findScanMatch: (buffer: string) => {
      // product scan
      setScanState("success");
      setScannedBarcode(buffer);
      const barcodeValue = getBarcodeValue(buffer);
      mixpanelTrack({
        trackedPageName,
        type: "Scan",
        label: "Scan Product",
        eventProperties: {
          Barcode: barcodeValue
        }
      });
      dispatch(setPage(1));
      dispatch(setSearch(barcodeValue));
      return barcodeValue;
    }
  });

  // if there are no putaway tasks returned, check backend to see if the product exists at all
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (putawayInventory?.length === 0 && scannedBarcode) {
      const searchProductFunc = async () => {
        const hits = await searchProduct(scannedBarcode);
        if (!hits.length) {
          errorToast(t("scanned product does not exist"));
        }
      };
      void searchProductFunc();
    }
  }, [errorToast, putawayInventory, scannedBarcode, t]);

  useEffect(() => {
    setMenuItems([
      {
        textContent: t("refresh"),
        trackingLabel: "Refresh",
        actionCb: async () => {
          await refetch();
        }
      }
    ]);
  }, [navigate, refetch, setMenuItems, t]);

  useEffect(() => {
    if (selectedPutawayTasks.length > 0) {
      setToolbar(<PutawayToolbar limit={limit} />);
    } else {
      setToolbar(null);
    }
  }, [limit, selectedPutawayTasks.length, setToolbar]);

  useEffect(() => {
    return () => {
      dispatch(clearSelectedPutawayTasks());
    };
  }, [dispatch]);

  const sortedPutawayInventory = putawayInventory?.slice().sort((a, b) => {
    // prioritize non-backstock bins if isHighPriority is the same
    if (
      a.target &&
      b.target &&
      a.target.isHighPriority === b.target.isHighPriority
    ) {
      return a.target.bin.binType !== "back stock" ? -1 : 0;
    }
    // otherwise, prioritize high priority
    return a.target && a.target.isHighPriority ? -1 : 0;
  });

  const tasksItemCount = sortedPutawayInventory?.reduce(
    (acc: number, i: PutAwayTaskSummaryDto) => acc + i.remaining.value,
    0
  );

  const handlePutAwayRowClick = (putAwayTaskId: string) => {
    dispatch(
      selectPutawayTasks(
        selectedPutawayTasks.includes(putAwayTaskId)
          ? [...selectedPutawayTasks.filter((id) => id !== putAwayTaskId)]
          : [...selectedPutawayTasks, putAwayTaskId]
      )
    );
  };

  return (
    <>
      <Container sx={{ my: 2 }}>
        {!!error && <ErrorPanel message={getMessageFromRtkError(error)} />}
        <ASTableV2
          gridTemplateColumns={isMobile ? "repeat(3, auto)" : "repeat(5, auto)"}
          isLoading={false}
          pagination={{
            pageCount: totalPageCount,
            page,
            setPage: (p) => {
              dispatch(setPage(p));
              if (window.scrollTo) window.scrollTo(0, 0);
            }
          }}
        >
          <ASTableV2Header>
            <ASTableV2Row>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("product")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("target bin")}</Typography>
              </ASTableV2Cell>
              {!isMobile && (
                <ASTableV2Cell>
                  <Typography variant="tableBody">
                    {t("received date")}
                  </Typography>
                </ASTableV2Cell>
              )}
              {!isMobile && (
                <ASTableV2Cell>
                  <Typography variant="tableBody">
                    {t("purchase order")}
                  </Typography>
                </ASTableV2Cell>
              )}
              <ASTableV2Cell>
                <Typography variant="tableBody">{`(${tasksItemCount}) ${t("items")}`}</Typography>
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Header>
          <ASTableV2Body>
            {isFetching && (
              <ASTableV2RowSkeleton columns={isMobile ? 3 : 5} rows={4} />
            )}
            {!isFetching && !sortedPutawayInventory?.length && (
              <ASTableV2RowEmpty>{t("no results")}</ASTableV2RowEmpty>
            )}
            {sortedPutawayInventory?.map((row) => {
              const { isHighPriority } = row.target || {};
              const { productName } = row.product || {};
              const isRowSelected = selectedPutawayTasks.includes(
                row.putAwayTaskId
              );

              const {
                aisle = "",
                shelfBay = "",
                shelf = "",
                position = "",
                temperatureZone,
                binType
              } = row.target?.bin || {};

              const isBulkTarget = binType === "Bulk";

              return (
                <ASTableV2Row
                  key={row.putAwayTaskId}
                  selected={isRowSelected}
                  onClick={() => {
                    mixpanelTrack({
                      trackedPageName,
                      type: isRowSelected ? "Unselect Row" : "Select Row",
                      label: isRowSelected
                        ? "Deselect Putaway Task"
                        : "Select Putaway Task",
                      eventProperties: {
                        "Putaway Task ID": row.putAwayTaskId,
                        "Product Name": name
                      }
                    });
                    handlePutAwayRowClick(row.putAwayTaskId);
                  }}
                >
                  <ASTableV2Cell
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start"
                    }}
                  >
                    <Typography variant="tableBody">{productName}</Typography>
                    {isMobile && (
                      <>
                        <Typography variant="tableBody" color="textSecondary">
                          {formatUtcDate(row.createdTimestamp)}
                        </Typography>
                        {row.purchaseOrderLineItem?.purchaseOrderNumber && (
                          <Typography variant="tableBody" color="textSecondary">
                            {row.purchaseOrderLineItem.purchaseOrderNumber}
                          </Typography>
                        )}
                      </>
                    )}
                    {isHighPriority && (
                      <WarningRounded
                        sx={{
                          color: "warning.main",
                          verticalAlign: "bottom"
                        }}
                      />
                    )}
                  </ASTableV2Cell>
                  <ASTableV2Cell
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}
                  >
                    <Typography variant="tableBody">
                      {[aisle, shelfBay, shelf, position]
                        .filter(Boolean)
                        .join("-")}
                    </Typography>
                    {isBulkTarget && (
                      <Typography component="div" variant="tableBody">
                        <BulkBoxIcon
                          tempZone={
                            temperatureZone?.toLowerCase() as
                              | "chilled"
                              | "ambient"
                              | "frozen"
                          }
                        />
                      </Typography>
                    )}
                  </ASTableV2Cell>
                  {!isMobile && (
                    <ASTableV2Cell>
                      <Typography>
                        {formatUtcDate(row.createdTimestamp) ?? ""}
                      </Typography>
                    </ASTableV2Cell>
                  )}
                  {!isMobile && (
                    <ASTableV2Cell>
                      <Typography>
                        {row.purchaseOrderLineItem?.purchaseOrderNumber ?? ""}
                      </Typography>
                    </ASTableV2Cell>
                  )}
                  <ASTableV2Cell>
                    <Typography variant="tableBody">
                      {row.remaining.value}
                    </Typography>
                  </ASTableV2Cell>
                </ASTableV2Row>
              );
            })}
          </ASTableV2Body>
        </ASTableV2>
      </Container>

      <ScanningIndicator
        scanState={scanState}
        scannedBarcode={scannedBarcode}
        placeholderText="Scan Product"
      />
    </>
  );
};
